<script setup lang="ts">
import type { TextFieldProps } from '@formwerk/core'
import { useFormContext, useTextField } from '@formwerk/core'

type DateFieldProps = Omit<TextFieldProps, 'type'> & {
  min?: string
  max?: string
}

const props = defineProps<DateFieldProps>()

// Formwerk's text field only types the "text-like" inputs, but it passes `type` straight
// through to the DOM, so a native date input keeps the value as a plain `YYYY-MM-DD` string.
const { inputProps, labelProps, errorMessage, errorMessageProps, fieldValue, isBlurred } = useTextField({
  ...toRefs(props),
  // `schema` must stay a plain value: formwerk doesn't unwrap it, and a ref makes every validation throw.
  schema: props.schema,
  type: 'date' as TextFieldProps['type'],
})

const isDirty = computed(() => fieldValue.value?.length > 0)
// Inside a form, errors only show after a submit attempt, then update live as the user fixes them
const form = useFormContext()
const errored = computed(() => (form ? form.isSubmitAttempted.value : isBlurred.value) && errorMessage.value)
</script>

<template>
  <div class="input">
    <div class="w-full relative" :class="{ 'c-red': errored }">
      <input
        v-bind="inputProps"
        :min="min"
        :max="max"
        class="dateField-input peer typo-sans-1 c-current leading-5 pb-0.75 pt-3 appearance-none border-b border-current bg-transparent h-9 w-full block uppercase"
        :class="{ 'is-empty': !isDirty }"
      >

      <label
        v-bind="labelProps"
        class="typo-sans-1 leading-5 motion-snug pointer-events-none select-none uppercase left-0 top-3 absolute peer-focus:scale-80 peer-focus:-translate-x-10% peer-focus:-translate-y-4"
        :class="{
          '-translate-y-4 scale-80 -translate-x-10%': isDirty,
        }"
      >
        {{ label }}
      </label>
    </div>
    <TransitionDefault>
      <p
        v-if="errored"
        v-bind="errorMessageProps"
        class="text-red-500 typo-sans-1 mt-1"
      >
        {{ errorMessage }}
      </p>
    </TransitionDefault>
  </div>
</template>

<style scoped>
/* The native format mask would overlap the resting label: only show it on focus or once filled */
.dateField-input.is-empty:not(:focus)::-webkit-datetime-edit {
  opacity: 0;
}

/* The native picker icon is always black: redraw it as a mask so it follows the field's color */
.dateField-input::-webkit-calendar-picker-indicator {
  background-color: currentColor;
  background-image: none;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='black' stroke-width='1.25'%3E%3Crect x='1.5' y='2.5' width='13' height='12' rx='1'/%3E%3Cpath d='M1.5 6.5h13M5 1v3M11 1v3'/%3E%3C/svg%3E")
    center / contain no-repeat;
  width: 12px;
  height: 12px;
  padding: 0;
  margin-bottom: 2px;
  cursor: pointer;
}
</style>
